/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * What to ask the server again after something changes, said once per kind
 * of change.
 *
 * Refetching every query after every action was simple and wrong in both
 * directions: it asked for far more than had changed, and on a delete it asked
 * for the very agreement that was gone. A hand-written list per call site was
 * the opposite failure -- one cancel refreshed the dashboard and the other did
 * not. Each action now names the change, and this says what follows from it.
 */

async function invalidate(client: QueryClient, keys: readonly QueryKey[]): Promise<void> {
  await Promise.all(keys.map((queryKey) => client.invalidateQueries({ queryKey })));
}

/** One agreement's own reads, everything keyed by its id. */
const agreementKeys = (id: string): QueryKey[] => [
  ['agreement', id],
  ['activity', id],
  ['signers', id],
  ['signer-changes', id],
  ['approver-candidates', id],
  ['agreement-document-view', id],
  ['obligations', id],
];

/** Everything that lists or counts agreements, any of which one change can move. */
const AGREEMENT_LISTS: QueryKey[] = [
  ['agreements'],
  ['dashboard'],
  ['deadlines'],
  ['counterparties'],
  ['packets'],
  ['packet'],
  ['packet-waiting'],
  ['search'],
];

/** One agreement changed: its own page, and every list it appears in. */
export function agreementChanged(client: QueryClient, id: string): Promise<void> {
  return invalidate(client, [...agreementKeys(id), ...AGREEMENT_LISTS]);
}

/** Several agreements changed at once, as a bulk action does. */
export function agreementsChanged(
  client: QueryClient,
  ids: readonly string[],
): Promise<void> {
  return invalidate(client, [...ids.flatMap(agreementKeys), ...AGREEMENT_LISTS]);
}

/**
 * An agreement is gone. Its own reads are dropped rather than asked again --
 * asking would only fetch a 404 and flash "Not found" on the page that is
 * leaving -- and the lists it was in are asked again.
 */
export function agreementGone(client: QueryClient, id: string): void {
  for (const queryKey of agreementKeys(id)) client.removeQueries({ queryKey });
}

/** The lists an agreement was in, after one was added or removed. */
export function agreementListsChanged(client: QueryClient): Promise<void> {
  return invalidate(client, AGREEMENT_LISTS);
}

/** Agreements arrived by import: the lists they now appear in. */
export function importsChanged(client: QueryClient): Promise<void> {
  return invalidate(client, [['workflows'], ...AGREEMENT_LISTS]);
}

/** A packet started or changed: the packets, the bundles' counts, and the lists. */
export function packetsChanged(client: QueryClient): Promise<void> {
  return invalidate(client, [['packet-workflows'], ...AGREEMENT_LISTS]);
}

/**
 * A workflow changed: the list, its own reads, and what counts or offers
 * workflows. Leave out the id when one was created or removed.
 */
export function workflowChanged(client: QueryClient, id?: string): Promise<void> {
  const own: QueryKey[] = id
    ? [
        ['workflow', id],
        ['workflow-versions', id],
        ['workflow-preview', id],
      ]
    : [['workflow'], ['workflow-versions'], ['workflow-preview']];
  return invalidate(client, [['workflows'], ['packet-workflows'], ['dashboard'], ...own]);
}

/**
 * What draws the letterhead: a document shown from an agreement, and a
 * workflow's preview. There is no letterhead query of its own to refresh.
 */
export function letterheadChanged(client: QueryClient): Promise<void> {
  return invalidate(client, [['agreement-document-view'], ['workflow-preview']]);
}

/** Counterparties merged: the groups left, and every list that names one (`CQ-09`). */
export function counterpartiesMerged(client: QueryClient): Promise<void> {
  return invalidate(client, [['counterparty-duplicates'], ...AGREEMENT_LISTS]);
}
